import React, { useState } from 'react';
import { ArrowLeft, Download, Share2, Users, FileCode, Lightbulb, Lock, Calendar, Eye, ImageIcon } from 'lucide-react';

const ExportingSharingDesignsArticle = ({ onBack }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button 
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-black mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Help Center
      </button>

      <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-lg mb-8">
        <div className="flex items-start">
          <Share2 className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-bold mb-3">Exporting and Sharing Your Designs</h1>
            <p className="text-gray-700">
              Learn how to export your prototypes as HTML, create shareable links for team members, and integrate your designs 
              into your development workflow. This guide will help you effectively communicate your design decisions with stakeholders
              and ensure your prototypes can be easily viewed by others.
            </p>
          </div>
        </div>
      </div>

      {!expanded ? (
        <div>
          <p className="text-gray-700 mb-6">
            Once you've created your prototypes, you'll want to share them with your team, stakeholders, or developers. 
            Our platform offers multiple ways to export and share your work, from downloadable HTML files to secure shareable links, 
            making it easy to collaborate and move your designs toward implementation.
          </p>
          <button
            onClick={() => setExpanded(true)}
            className="text-black font-medium hover:underline"
          >
            Read more
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Download className="w-5 h-5 mr-2 text-gray-700" />
              Exporting Views as HTML
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                You can export any view as a standalone HTML file to share or integrate into your development process:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Open the view</strong> you want to export from your project dashboard.
                </li>
                <li>
                  <strong>Click the download icon</strong> in the top navigation bar of the view screen.
                </li>
                <li>
                  <strong>Save the HTML file</strong> to your desired location.
                </li>
                <li>
                  <strong>The downloaded file includes</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>All HTML structure for your design</li>
                    <li>Embedded CSS styling</li>
                    <li>Any JavaScript interactions defined in your prototype</li>
                    <li>References to necessary web fonts</li>
                    <li>Placeholder images for media elements</li>
                  </ul>
                </li>
              </ol>
              <p className="mt-4">
                The exported HTML file is self-contained and can be opened in any modern web browser without 
                requiring an internet connection or access to our platform.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Share2 className="w-5 h-5 mr-2 text-gray-700" />
              Creating Shareable Links
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Shareable links allow others to view your prototypes directly in their browser without needing an account:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>For individual views</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Open the view you want to share</li>
                    <li>Click the share icon in the navigation bar</li>
                    <li>Configure your sharing options (see below)</li>
                    <li>Click "Generate Share Link"</li>
                    <li>Copy the generated link to your clipboard</li>
                  </ul>
                </li>
                <li>
                  <strong>For flows</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Open the flow you want to share</li>
                    <li>Click the share icon in the flow viewer</li>
                    <li>Configure sharing options</li>
                    <li>Generate and copy the link</li>
                  </ul>
                </li>
                <li>
                  <strong>Shareable link options</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li><strong>Expiration</strong> <Calendar className="w-3 h-3 inline-block" /> - Choose never, 1 day, 7 days, 30 days, or 90 days</li>
                    <li><strong>Password protection</strong> <Lock className="w-3 h-3 inline-block" /> - Add an optional password that viewers must enter</li>
                    <li><strong>Version selection</strong> - For views, share the current version specifically</li>
                  </ul>
                </li>
              </ol>
              <p className="mt-4">
                Anyone with the shareable link can access your prototype in view-only mode. They don't need to 
                create an account or log in, making it easy to share with clients and stakeholders.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <ImageIcon className="w-5 h-5 mr-2 text-gray-700" />
              Sharing with Media Assets
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                When sharing views that contain images and videos, here's what you need to know:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Images are included</strong> - Any images you've uploaded to your view will be visible to anyone viewing your shared link.
                </li>
                <li>
                  <strong>Videos play embedded</strong> - Video embeds from YouTube, Vimeo, and Dailymotion will play directly in the shared view.
                </li>
                <li>
                  <strong>Responsive design</strong> - Shared views adapt to the viewer's device, just like in your preview.
                </li>
                <li>
                  <strong>Device simulation</strong> - Mobile designs will be displayed in a phone-like container for desktop viewers.
                </li>
                <li>
                  <strong>View limits</strong> <Eye className="w-3 h-3 inline-block" /> - Each share link has a maximum of 200 views for security purposes.
                </li>
              </ul>
              <p className="mt-4">
                For best results, make sure to add all necessary images and videos before creating your share link.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Users className="w-5 h-5 mr-2 text-gray-700" />
              Collaboration Workflow
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Effective collaboration enhances the value of your prototypes:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Share links in collaboration tools</strong> - Paste shareable links directly into Slack, Teams, or project management software.
                </li>
                <li>
                  <strong>Using different link types for different audiences</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>For clients - Password-protected links with expiration dates</li>
                    <li>For internal teams - Non-expiring links for ongoing reference</li>
                    <li>For temporary feedback - Short expiration links (1-7 days)</li>
                  </ul>
                </li>
                <li>
                  <strong>Present prototypes in meetings</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Use flow view for step-by-step presentation</li>
                    <li>Toggle between different versions to show design evolution</li>
                    <li>Use the fullscreen mode for cleaner presentations</li>
                  </ul>
                </li>
                <li>
                  <strong>Managing feedback</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Create new versions based on feedback</li>
                    <li>Generate new share links for updated designs</li>
                    <li>Keep track of which stakeholders have which links</li>
                  </ul>
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <FileCode className="w-5 h-5 mr-2 text-gray-700" />
              From Design to Development
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Bridge the gap between design and development with these approaches:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Export HTML for developers</strong> - Provide the exported HTML files to developers as reference implementation.
                </li>
                <li>
                  <strong>Extract CSS variables and values</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Colors and color schemes</li>
                    <li>Font families and sizes</li>
                    <li>Spacing and layout values</li>
                    <li>Transition and animation settings</li>
                  </ul>
                </li>
                <li>
                  <strong>Document interactive behaviors</strong> - Explicitly describe expected interactions, transitions, and states.
                </li>
                <li>
                  <strong>Create a shared design system</strong> - Document recurring UI patterns to ensure consistency.
                </li>
                <li>
                  <strong>Use shareable links for reference</strong> - Provide developers with access to the live, interactive prototype.
                </li>
              </ul>
              <p className="mt-4">
                The HTML exported from our platform is clean and well-structured, making it a valuable reference for developers 
                implementing your designs. While they may not use the exact code, it provides clear guidance on structure, 
                styling, and behavior.
              </p>
            </div>
          </section>

          <section className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
              Best Practices for Sharing
            </h2>
            <div className="text-gray-700">
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Share flows instead of isolated views</strong> when possible to provide context.</li>
                <li><strong>Include a brief description</strong> with shareable links to set expectations.</li>
                <li><strong>Specify which version is the "current" design</strong> when sharing multiple iterations.</li>
                <li><strong>Test shareable links yourself</strong> before sending to ensure they work as expected.</li>
                <li><strong>Use password protection</strong> for sensitive designs or when sharing outside your organization.</li>
                <li><strong>Set appropriate expiration dates</strong> based on the feedback timeline.</li>
                <li><strong>Create new share links</strong> after significant updates rather than relying on viewers to refresh.</li>
                <li><strong>Consider view limits</strong> when sharing with large audiences (max 200 views per link).</li>
                <li><strong>Use the copy button</strong> to ensure you're sharing the exact generated URL.</li>
              </ul>
            </div>
          </section>

          <button
            onClick={() => setExpanded(false)}
            className="text-black font-medium hover:underline mt-4 block"
          >
            Show less
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportingSharingDesignsArticle;